import type { ListFieldFormatting } from '@ardoq/api-types';
import { Text } from '@ardoq/typography';
import { Stack } from '@ardoq/layout';
import { noop } from 'lodash';
import {
  buildColorWarningKey,
  type FormattingContextState,
  ListFieldFormattingRuleColorRow,
} from '@ardoq/perspectives';
import type { SidebarFormattingRuleBodyProperties } from './types';
import { RowHeader } from '../../../atoms';
import { InvertCheckbox } from './atoms';

type ColorRowContext = {
  ruleId: string;
  fieldValue: string;
  listFieldFormatting: ListFieldFormatting;
  formattingContext: FormattingContextState;
};

const rowColorValueChange = (args: ColorRowContext) => {
  const {
    ruleId,
    fieldValue,
    listFieldFormatting,
    formattingContext: { updateRule },
  } = args;
  return (newColor: string) =>
    updateRule(ruleId, {
      value: {
        ...listFieldFormatting,
        [fieldValue]: {
          color: newColor,
          isDisabled: Boolean(listFieldFormatting[fieldValue]?.isDisabled),
        },
      },
    });
};

const rowIsDisabledToggle = (args: ColorRowContext) => {
  const {
    ruleId,
    fieldValue,
    listFieldFormatting,
    formattingContext: { updateRule },
  } = args;
  return () =>
    updateRule(ruleId, {
      value: {
        ...listFieldFormatting,
        [fieldValue]: {
          color: listFieldFormatting[fieldValue]?.color || '',
          isDisabled: !listFieldFormatting[fieldValue]?.isDisabled,
        },
      } satisfies ListFieldFormatting,
    });
};

/** The body of a List field formatting rule in the Perspectives Sidebar. */
const ListFieldFormattingRuleBody = ({
  rule,
  formattingContext,
  colorPaletteAlignment,
}: SidebarFormattingRuleBodyProperties) => {
  const { color, __id: ruleId } = rule;
  const { ruleInfo } = formattingContext;
  const info = ruleInfo[ruleId];
  if (!info?.listFieldFormatting) {
    return null;
  }
  const { listFieldFormatting, colorWarnings } = info;

  return (
    <>
      <RowHeader>Value</RowHeader>
      <Text variant="caption" color="textSubtle">
        All list field options are automatically added with colors. You can
        change or hide any of the list field options.
      </Text>
      <Stack gap="small">
        {Object.keys(listFieldFormatting).map(fieldValue => {
          const context: ColorRowContext = {
            ruleId,
            fieldValue,
            listFieldFormatting,
            formattingContext,
          };
          const itemColor = listFieldFormatting[fieldValue]?.color;
          const colorWarning = itemColor
            ? colorWarnings[buildColorWarningKey(itemColor, fieldValue)]
            : undefined;

          return (
            <ListFieldFormattingRuleColorRow
              key={`${fieldValue}:${color}`}
              fieldValue={fieldValue}
              listFieldFormatting={listFieldFormatting}
              onColorValueChange={rowColorValueChange(context)}
              onIsDisabledToggle={rowIsDisabledToggle(context)}
              colorWarning={colorWarning}
              colorPaletteAlignment={colorPaletteAlignment}
            />
          );
        })}
      </Stack>
      {rule.isInverted && <InvertCheckbox isInverted onChange={noop} />}
    </>
  );
};
export { ListFieldFormattingRuleBody };
